import React from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Typography,
  alpha,
  Checkbox,
  Tooltip,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import ProductSelect from '../../features/invoices/components/ProductSelect';

/**
 * DocumentProductsTable Component
 * Displays product table with add, edit quantity/price, and remove functionality
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentProductsTable = ({
  products = [],
  onProductAdd,
  onProductRemove,
  onQuantityChange,
  onPriceChange,
  onTotalInclChange,
  onAddProductClick,
  loadProductOptions,
  vatPercentage,
  showVatColumn = true,
  showTotalExclColumn = true,
  showTotalInclColumn = true,
  convertToInvoiceMode = false,
  selectedLineItems = [],
  onLineItemToggle,
  onLineItemSelectAll,
  showProfit = false,
  onCustomProductOptionSelect,
  externalPendingProduct,
  onExternalPendingConsumed,
}) => {
  const { t } = useTranslation();
  const [pendingProduct, setPendingProduct] = React.useState(null);
  const [pendingQuantity, setPendingQuantity] = React.useState(1);
  const [pendingPrice, setPendingPrice] = React.useState('');
  const [pendingError, setPendingError] = React.useState('');
  const [selectKey, setSelectKey] = React.useState(0);

  // Consume external pending product (e.g. newly created product from dialog)
  React.useEffect(() => {
    if (externalPendingProduct) {
      setPendingProduct(externalPendingProduct);
      setPendingQuantity(1);
      setPendingPrice(externalPendingProduct.SalePrice || externalPendingProduct.Price || '');
      setPendingError('');
      if (onExternalPendingConsumed) onExternalPendingConsumed();
    }
  }, [externalPendingProduct]);
  
  const handleAddProduct = () => {
    if (!onProductAdd || !pendingProduct) return;

    // Validate price > 0 (for all products including temp)
    if (!pendingPrice || parseFloat(pendingPrice) <= 0) {
      setPendingError(t('please_enter_valid_price') || 'Please enter a valid price greater than 0');
      return;
    }
    if (!pendingQuantity || parseInt(pendingQuantity) <= 0) {
      setPendingError(t('please_enter_valid_quantity') || 'Please enter a valid quantity greater than 0');
      return;
    }

    setPendingError('');
    onProductAdd(pendingProduct, pendingQuantity, pendingPrice);
    setPendingProduct(null);
    setPendingQuantity(1);
    setPendingPrice('');
    // Force ProductSelect to remount and get focus
    setSelectKey(prev => prev + 1);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'visible',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              {convertToInvoiceMode && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5,
                  textAlign: 'center',
                  width: '60px'
                }}>
                  <Checkbox
                    checked={selectedLineItems.length === products.length && products.length > 0}
                    onChange={(e) => onLineItemSelectAll && onLineItemSelectAll(e.target.checked)}
                    sx={{ 
                      color: 'white',
                      '&.Mui-checked': { color: 'white' }
                    }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5 
              }}>
                {t("product_name") || "PRODUCT NAME"}
              </TableCell>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'center', 
                width: '120px' 
              }}>
                {t("quantity") || "QUANTITY"}
              </TableCell>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'right', 
                width: '120px' 
              }}>
                {t("price") || "PRICE"}
              </TableCell>
              {showVatColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '100px' 
                }}>
                  {t("vat") || "VAT"}
                </TableCell>
              )}
              {showTotalExclColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '130px' 
                }}>
                  {t("total_excl") || "TOTAL EXCL."}
                </TableCell>
              )}
              {showTotalInclColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '130px' 
                }}>
                  {t("total_incl") || "TOTAL INCL."}
                </TableCell>
              )}
              {showProfit && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '120px',
                  bgcolor: alpha('#4caf50', 0.2)
                }}>
                  {t("profit") || "PROFIT"}
                </TableCell>
              )}
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'center', 
                width: '100px' 
              }}>
                {t("actions") || "ACTIONS"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Added Products Rows */}
            {products.map((p) => {
              const vatPerUnit = (parseFloat(p.VATAmount || 0) / (p.Quantity || 1)).toFixed(2);
              const totalExcl = parseFloat(p.TotalExcl || 0);
              const totalIncl = parseFloat(p.TotalIncl || 0);
              const isSelected = selectedLineItems.includes(p._rowKey || p.Id);
              
              return (
                <TableRow 
                  key={p._rowKey || p.Id}
                  sx={{
                    '&:hover': { bgcolor: '#f8f9fa' },
                    borderBottom: '1px solid #e0e0e0',
                    bgcolor: convertToInvoiceMode && isSelected ? alpha('#667eea', 0.08) : 'transparent'
                  }}
                >
                  {convertToInvoiceMode && (
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onLineItemToggle && onLineItemToggle(p._rowKey || p.Id)}
                        sx={{
                          '&.Mui-checked': { color: '#667eea' }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ py: 1.5, fontSize: '0.85rem', color: '#333' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {p.Name || p.ProductName}
                      </Typography>
                      {p.isTemp && (
                        <Chip 
                          label={t('temp') || 'Temp'} 
                          size="small" 
                          color="warning"
                          sx={{ 
                            height: '20px', 
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }} 
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                    <Tooltip 
                      title={p._validationErrors?.quantity ? (t('please_enter_valid_quantity') || 'Please enter a valid quantity (greater than 0)') : ''}
                      open={p._validationErrors?.quantity ? undefined : false}
                      arrow
                    >
                      <TextField
                        value={p.Quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*$/.test(value)) {
                            const numValue = value === '' ? 1 : parseInt(value, 10);
                            onQuantityChange && onQuantityChange(p, numValue);
                          }
                        }}
                        size="small"
                        disabled={convertToInvoiceMode}
                        error={p._validationErrors?.quantity}
                        sx={{
                          width: '80px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'center', py: 0.5 },
                            ...(p._validationErrors?.quantity && {
                              '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                            })
                          }
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                    <Tooltip 
                      title={p._validationErrors?.price ? (t('please_enter_valid_price') || 'Please enter a valid price (greater than 0)') : ''}
                      open={p._validationErrors?.price ? undefined : false}
                      arrow
                    >
                      <TextField
                        value={p.Price}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const numValue = value === '' ? 0 : parseFloat(value);
                            onPriceChange && onPriceChange(p, numValue);
                          }
                        }}
                        size="small"
                        disabled={convertToInvoiceMode}
                        error={p._validationErrors?.price}
                        sx={{
                          width: '100px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 },
                            ...(p._validationErrors?.price && {
                              '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                            })
                          }
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  {showVatColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 500, color: '#f57c00' }}>
                      €{vatPerUnit}
                    </TableCell>
                  )}
                  {showTotalExclColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#388e3c' }}>
                      €{totalExcl.toFixed(2)}
                    </TableCell>
                  )}
                  {showTotalInclColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                      <TextField
                        type="number"
                        value={totalIncl.toFixed(2)}
                        onChange={(e) => onTotalInclChange && onTotalInclChange(p, parseFloat(e.target.value) || 0)}
                        size="small"
                        disabled={convertToInvoiceMode}
                        sx={{
                          width: '90px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'right', py: 0.5, fontWeight: 600 }
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  {showProfit && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#2e7d32' }}>
                      {(() => {
                        const costPrice = Number(p.CostPrice || p.BuyPrice || 0);
                        const quantity = Number(p.Quantity || 0);
                        const totalCost = costPrice * quantity;
                        const profit = totalExcl - totalCost;
                        return `€${profit.toFixed(2)}`;
                      })()}
                    </TableCell>
                  )}
                  <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => onProductRemove && onProductRemove(p)}
                      disabled={convertToInvoiceMode}
                      sx={{
                        color: '#d32f2f',
                        '&:hover': { bgcolor: alpha('#d32f2f', 0.1) },
                        '&:disabled': { color: '#ccc' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            
            {/* Product Search Row - For adding new products */}
            {!convertToInvoiceMode && (
              <TableRow sx={{ bgcolor: '#fffef0', borderTop: '2px solid #667eea' }}>
                {!pendingProduct ? (
                  <TableCell colSpan={7} sx={{ py: 2, position: 'relative', zIndex: 10 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {onAddProductClick && (
                        <IconButton
                          size="small"
                          onClick={onAddProductClick}
                          sx={{
                            color: '#667eea',
                            bgcolor: alpha('#667eea', 0.1),
                            '&:hover': { bgcolor: alpha('#667eea', 0.2) }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                      <Box sx={{ flex: 1, position: 'relative', zIndex: 20 }}>
                        <ProductSelect
                          key={selectKey}
                          value={null}
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              // Handle custom options
                              if (selectedOption.isCustomOption) {
                                if (selectedOption.value === '__add_new__') {
                                  // Open add product dialog immediately
                                  if (onCustomProductOptionSelect) {
                                    onCustomProductOptionSelect('add_new', selectedOption.searchText);
                                  }
                                  return;
                                } else if (selectedOption.value === '__temp_add__' && selectedOption.isTempProduct) {
                                  // Set as pending product with searchText as name
                                  setPendingProduct({
                                    Id: `temp_${Date.now()}`,
                                    Name: selectedOption.searchText,
                                    ProductName: selectedOption.searchText,
                                    Price: 0,
                                    SalePrice: 0,
                                    isTemp: true,
                                    searchText: selectedOption.searchText,
                                  });
                                  setPendingQuantity(1);
                                  setPendingPrice(0);
                                  return;
                                }
                              }
                              
                              // Regular product selection
                              const productPrice = selectedOption.SalePrice || selectedOption.Price || selectedOption.price || "";
                              setPendingProduct(selectedOption);
                              setPendingQuantity(1);
                              setPendingPrice(productPrice);
                            }
                          }}
                          loadOptions={loadProductOptions}
                          placeholder={t("search_products") || "Search and add products..."}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            menu: (base) => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                ) : (
                  <>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {pendingProduct.Name}
                        </Typography>
                        {pendingProduct.isTemp && (
                          <Chip 
                            label={t('temp') || 'Temp'} 
                            size="small" 
                            color="warning"
                            sx={{ 
                              height: '20px', 
                              fontSize: '0.7rem',
                              fontWeight: 600
                            }} 
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      <TextField
                        value={pendingQuantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*$/.test(value)) {
                            setPendingQuantity(value === '' ? 1 : parseInt(value, 10));
                            if (pendingError) setPendingError('');
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddProduct();
                          }
                        }}
                        size="small"
                        autoFocus={!pendingProduct?.isTemp}
                        error={pendingError && pendingError.includes('quantity')}
                        sx={{
                          width: '80px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'center', py: 0.5 }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                      <TextField
                        value={pendingPrice}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            setPendingPrice(value);
                            if (pendingError) setPendingError('');
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddProduct();
                          }
                        }}
                        size="small"
                        autoFocus={pendingProduct?.isTemp}
                        error={!!pendingError && !pendingError.includes('quantity')}
                        placeholder={pendingProduct?.isTemp ? '0.00' : ''}
                        sx={{
                          width: '100px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 },
                            ...(pendingError && !pendingError.includes('quantity') && {
                              '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                            })
                          }
                        }}
                      />
                    </TableCell>
                    {showVatColumn && <TableCell sx={{ py: 1.5 }}></TableCell>}
                    {showTotalExclColumn && <TableCell sx={{ py: 1.5 }}></TableCell>}
                    {showTotalInclColumn && <TableCell sx={{ py: 1.5 }}></TableCell>}
                    {showProfit && <TableCell sx={{ py: 1.5 }}></TableCell>}
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={handleAddProduct}
                          sx={{
                            color: '#4caf50',
                            bgcolor: alpha('#4caf50', 0.1),
                            '&:hover': { bgcolor: alpha('#4caf50', 0.2) }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPendingProduct(null);
                            setPendingQuantity(1);
                            setPendingPrice('');
                            setPendingError('');
                          }}
                          sx={{
                            color: '#d32f2f',
                            bgcolor: alpha('#d32f2f', 0.1),
                            '&:hover': { bgcolor: alpha('#d32f2f', 0.2) }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {/* Inline validation error below buttons */}
                      {pendingError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, textAlign: 'center', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                          {pendingError}
                        </Typography>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            )}
            
            {/* Totals Row - At the bottom */}
            {products.length > 0 && (
              <TableRow sx={{ bgcolor: '#f0f0f0', borderTop: '2px solid #667eea' }}>
                {convertToInvoiceMode && (
                  <TableCell sx={{ py: 1.5 }}></TableCell>
                )}
                <TableCell sx={{ py: 1.5, fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                  {t("total") || "TOTAL"}
                </TableCell>
                <TableCell sx={{ py: 1.5, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                  {products.reduce((sum, p) => sum + parseFloat(p.Quantity || 0), 0)}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}></TableCell>
                {showVatColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#f57c00' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.VATAmount || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                {showTotalExclColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#388e3c' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.TotalExcl || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                {showTotalInclColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.95rem', color: '#667eea' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.TotalIncl || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                <TableCell sx={{ py: 1.5 }}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default DocumentProductsTable;
